import {
    Button,
    Cascader,
    Checkbox,
    Form,
    Input,
    InputNumber,
    Radio,
    Select,
    Space,
    Tooltip,
    type FormInstance
} from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { type ChangeEvent, type RefObject, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    DatePicker as ZhDatePicker,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    GridItem as ZhGridItem
} from "zhihao-ui-react";
import type { MapViewForm } from "@zhihaoscm/entity";
import { FormTypeEnum } from "@zhihaoscm/meta";

import request from "@/api";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { enterpriseManageService } from "@/api/services/enterpriseManageService";
import { groupPurchaseService } from "@/api/services/groupPurchaseService";
import { prodTypeService } from "@/api/services/prodTypeService";
import {
    loadOilSiteAddressSuggestions,
    loadOilSiteAreaOptions,
    type OilSiteAddressSuggestion,
    type OilSiteAreaOption
} from "@/views/pages/logistics/oil/OilSiteFormPage";
import { sliceGbCode } from "@/views/pages/logistics/wharf";
import WangEditor, { type RichEditorRef } from "@/views/components/WangEditor";
import ZhMapDialog from "@/views/components/zhMapDialog";
import {
    DEFAULT_PRODUCT_PARAMETERS,
    DEFAULT_SERVICE_GUARANTEE,
    DEFAULT_SETTLEMENT_DESCN,
    extractProductEditorFileIds,
    formatDeliveryDateValue,
    FormSection,
    HandlerMobileEditor,
    OfflineAccountEditor,
    OnlineAccountTip,
    ParameterEditor,
    ProductAddressAutocomplete,
    ProductLabelEditor,
    ProductMediaUpload,
    validateLegacyProductDraft,
    type ProductMediaFile
} from "./ConsignmentFormPage";
import type { GroupPurchaseFormValues, GroupPurchaseRow } from "./types";
import "./ConsignmentFormPage.scss";

interface ParameterItem {
    displayOrder?: number;
    parameterName?: string;
    parameterValue?: string;
}

/** 团购 Vue 表单的完整提交字段，覆盖商品、提货、结算、媒体与动态参数。 */
type CompleteValues = GroupPurchaseFormValues & {
    imgMainId?: number;
    videoId?: number;
    priceType?: number;
    unitPrice?: number;
    isIncludeTax?: number;
    taxRate?: number;
    waitingPayTime?: number;
    groupSuccessTon?: number;
    floatType?: number;
    groupFloatRatio?: number;
    groupFloatTon?: number;
    supplierId?: number;
    productTypeId?: string;
    parameterInfo?: ParameterItem[];
    productLabel?: string[];
    deliveryStartTime?: string | null;
    deliveryEndTime?: string | null;
    provinceCode?: string;
    cityCode?: string;
    regionCode?: string;
    address?: string;
    geo?: { lng?: number; lat?: number };
    geoLng?: string;
    geoLat?: string;
    minAdjustmentTon?: number;
    deliveryDescn?: string;
    settlementMethod?: number;
    depositPaymentMethod?: number;
    paymentRatio?: number;
    fixedAmount?: number;
    payType?: number;
    payTypes?: number[];
    settlementDescn?: string;
    serviceGuarantee?: string;
    regulation?: string;
    handlerId?: number;
    handlerMobile?: string[];
    imgIds?: number[];
    activeFileIds?: number[];
    platformBankAccountForm?: Array<{
        name?: string;
        account?: string;
        openingBank?: string;
        isOwnAccount?: number;
        supportOnline?: number;
    }>;
};

type GroupPurchaseFormPayload = CompleteValues;
type SelectOption = { label: string; value: number | string };
type ProductSelectOption = SelectOption & { specificationModulus?: string; tags?: string[] };
type ProductExtension = Pick<
    CompleteValues,
    | "imgMainId"
    | "videoId"
    | "priceType"
    | "floatType"
    | "groupFloatRatio"
    | "groupFloatTon"
    | "supplierId"
    | "productTypeId"
    | "depositPaymentMethod"
    | "paymentRatio"
    | "fixedAmount"
>;

interface UploadedFile {
    id: number;
    path?: string;
}

const DEFAULT_GROUP_REGULATION =
    "1、团购专区选商品，点击 “参与拼团” 加入，每种商品每人仅能拼团一次​\n2、商品设有拼团截止时间，需在规定时间内参与。\n3、以 40 万吨（浮动 ±2 万吨）为例：拼团吨数达 38 万吨及以上即成功​\n4、截止时未达成功吨数（如不足 38 万吨），拼团失败，系统将自动取消全部订单并退款";

function parseGeoPoint(value?: string): [number, number] | undefined {
    const point = value?.split(",").map(item => Number(item.trim()));
    return point?.length === 2 && point.every(Number.isFinite) ? [point[0], point[1]] : undefined;
}

export interface GroupPurchaseFormPageProps {
    loadGroupPurchase?: (id: number | string) => Promise<GroupPurchaseRow>;
    saveGroupPurchase?: (id: number | string | undefined, values: GroupPurchaseFormPayload) => Promise<void>;
    submitGroupPurchase?: (id: number | string | undefined, values: GroupPurchaseFormPayload) => Promise<void>;
    loadAddressSuggestions?: (keyword: string, regionCode?: string) => Promise<OilSiteAddressSuggestion[]>;
}

/** 复用 Vue 团购商品详情接口。 */
export async function loadGroupPurchase(id: number | string): Promise<GroupPurchaseRow> {
    const response = await request.service.get<GroupPurchaseRow>(`/admin/product-group-purchase/vo/${id}`);
    return response.data;
}

/** 复用 Vue 团购商品草稿接口。 */
export async function saveGroupPurchase(
    id: number | string | undefined,
    values: GroupPurchaseFormPayload
): Promise<void> {
    if (id == null) await request.service.post("/admin/product-group-purchase/create-save", values);
    else await request.service.post(`/admin/product-group-purchase/update-save/${id}`, values);
}

/** 复用 Vue 团购商品正式提交接口。 */
export async function submitGroupPurchase(
    id: number | string | undefined,
    values: GroupPurchaseFormPayload
): Promise<void> {
    if (id == null) await request.service.post("/admin/product-group-purchase", values);
    else await request.service.put(`/admin/product-group-purchase/${id}`, values);
}

/** 复用 Vue 上传组件的 FormData.file 契约。 */
export async function uploadGroupPurchaseMedia(file: Blob): Promise<UploadedFile> {
    const formData = new FormData();
    formData.append("file", file);
    const { fileService } = await import("@/api/services/fileService");
    const uploaded = await fileService.uploadFile(formData);
    if (!uploaded.id) throw new Error("上传响应缺少文件 ID");
    return { id: Number(uploaded.id), path: uploaded.path };
}

/** 富文本使用项目 WangEditor，隐藏字段只负责接入 Ant Form。 */
function RichTextItem({
    form,
    name,
    label,
    required = false,
    editorRef
}: {
    form: FormInstance<CompleteValues>;
    name: keyof CompleteValues;
    label: string;
    required?: boolean;
    editorRef?: RefObject<RichEditorRef | null>;
}) {
    const value = Form.useWatch(name, form);
    return (
        <div className="consignment-form__rich-row">
            <label className={`consignment-form__label${required ? " consignment-form__label--required" : ""}`}>
                {label}
            </label>
            <div className="consignment-form__rich-content">
                <WangEditor
                    ref={editorRef}
                    value={typeof value === "string" ? value : ""}
                    height="300px"
                    onUpdateValue={next => form.setFieldValue(name, next)}
                />
                <Form.Item name={name} noStyle>
                    <Input.TextArea aria-label={label} className="consignment-form__legacy-control" />
                </Form.Item>
            </div>
        </div>
    );
}

/** 价格类型说明与旧版单价组件一致。 */
function PriceTip({ type }: { type: 1 | 2 }) {
    const title =
        type === 1 ? "固定价格：为实际交易价格，不受市场行情影响" : "预估价格：价格可能存在变动，以提货当日价格为准";
    return (
        <Tooltip title={title}>
            <button
                className="consignment-form__price-tip"
                type="button"
                aria-label={`${type === 1 ? "固定" : "预估"}价格说明`}
            >
                <svg viewBox="0 0 18 18" aria-hidden="true">
                    <circle cx="9" cy="9" r="7" />
                    <path d="M9 8.2v4.2M9 5.4h.01" />
                </svg>
            </button>
        </Tooltip>
    );
}

/** 把编辑详情的数组和坐标字段转换为局部编辑器值。 */
function normalizeValues(row: GroupPurchaseRow): CompleteValues {
    const product = row.productGroupPurchase as GroupPurchaseRow["productGroupPurchase"] & ProductExtension;
    return {
        ...product,
        parameterInfo: product.parameterInfo?.length
            ? product.parameterInfo
            : DEFAULT_PRODUCT_PARAMETERS.map(item => ({ ...item })),
        productLabel: product.productLabel?.length ? product.productLabel : [""],
        handlerMobile: product.handlerMobile?.length ? product.handlerMobile : [""],
        introduce: product.introduce || "",
        deliveryDescn: product.deliveryDescn || "",
        settlementDescn: product.settlementDescn || "",
        serviceGuarantee: product.serviceGuarantee || "",
        regulation: product.regulation || "",
        ...(product.geo ? { geoLng: String(product.geo.lng ?? ""), geoLat: String(product.geo.lat ?? "") } : {}),
        ...(product.payType == null
            ? {}
            : { payTypes: Number(product.payType) === 3 ? [1, 2] : [Number(product.payType)] })
    } as CompleteValues;
}

/** 团购商品新增/编辑页，恢复 Vue 的五个完整业务分区。 */
export function GroupPurchaseFormPage({
    loadGroupPurchase: load = loadGroupPurchase,
    saveGroupPurchase: save = saveGroupPurchase,
    submitGroupPurchase: submitForm = submitGroupPurchase,
    loadAddressSuggestions = loadOilSiteAddressSuggestions
}: GroupPurchaseFormPageProps) {
    const navigate = useNavigate();
    const query = new URLSearchParams(useLocation().search);
    const id = query.get("id") || undefined;
    const copyId = query.get("copyId") || undefined;
    const loadId = id || copyId;
    const [form] = Form.useForm<CompleteValues>();
    const priceType = Form.useWatch("priceType", form);
    const isIncludeTax = Form.useWatch("isIncludeTax", form);
    const floatType = Form.useWatch("floatType", form);
    const settlementMethod = Form.useWatch("settlementMethod", form);
    const depositPaymentMethod = Form.useWatch("depositPaymentMethod", form);
    const supplierId = Form.useWatch("supplierId", form);
    const productTypeId = Form.useWatch("productTypeId", form);
    const handlerId = Form.useWatch("handlerId", form);
    const payTypes = Form.useWatch("payTypes", form);
    const regionCode = Form.useWatch("regionCode", form);
    const geoLng = Form.useWatch("geoLng", form);
    const geoLat = Form.useWatch("geoLat", form);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [imageFiles, setImageFiles] = useState<ProductMediaFile[]>([]);
    const [videoFiles, setVideoFiles] = useState<ProductMediaFile[]>([]);
    const [supplierOptions, setSupplierOptions] = useState<SelectOption[]>([]);
    const [productTypeOptions, setProductTypeOptions] = useState<ProductSelectOption[]>([]);
    const [productTypeTags, setProductTypeTags] = useState<string[]>([]);
    const [handlerOptions, setHandlerOptions] = useState<SelectOption[]>([]);
    const [supplierLoading, setSupplierLoading] = useState(false);
    const [productTypeLoading, setProductTypeLoading] = useState(false);
    const [handlerLoading, setHandlerLoading] = useState(false);
    const [publishState, setPublishState] = useState<number>();
    const [offlineAccounts, setOfflineAccounts] = useState<NonNullable<CompleteValues["platformBankAccountForm"]>>([
        {}
    ]);
    const [mapDialogOpen, setMapDialogOpen] = useState(false);
    const [areaOptions, setAreaOptions] = useState<OilSiteAreaOption[]>([]);
    const [mapData, setMapData] = useState<MapViewForm>({
        id: 0,
        areaCode: [],
        address: "",
        province: "",
        city: "",
        region: "",
        detailAddress: "",
        lngLat: "",
        draggable: true
    });
    const introduceEditorRef = useRef<RichEditorRef>(null);
    const initialEditorFileIdsRef = useRef<number[]>([]);

    const ensureAreaOptions = async () => {
        if (!areaOptions.length) setAreaOptions(await loadOilSiteAreaOptions());
    };

    /** 地址联想项按旧 address-item 一次回填地区、详细地址和经纬度。 */
    const selectAddressSuggestion = (suggestion: OilSiteAddressSuggestion) => {
        const point = parseGeoPoint(suggestion.lonlat);
        const areaCodes = suggestion.countyCode ? sliceGbCode(suggestion.countyCode) : [];
        form.setFieldsValue({
            address: suggestion.address || suggestion.value,
            ...(areaCodes.length
                ? { provinceCode: areaCodes[0], cityCode: areaCodes[1], regionCode: areaCodes[2] }
                : {}),
            ...(point ? { geoLng: String(point[0]), geoLat: String(point[1]) } : {})
        });
    };

    useEffect(() => {
        if (!loadId) {
            form.setFieldsValue({
                title: "",
                weight: 0,
                parameterInfo: DEFAULT_PRODUCT_PARAMETERS.map(item => ({ ...item })),
                productLabel: [""],
                handlerMobile: [""],
                payTypes: [1],
                settlementDescn: DEFAULT_SETTLEMENT_DESCN,
                serviceGuarantee: DEFAULT_SERVICE_GUARANTEE,
                regulation: DEFAULT_GROUP_REGULATION
            });
            return;
        }
        void load(loadId)
            .then(row => {
                const values = normalizeValues(row);
                form.setFieldsValue(values);
                setImageFiles(
                    [row.mainFile, ...((row.otherImgFile || []) as ProductMediaFile[])]
                        .filter((file): file is ProductMediaFile => Boolean(file?.id))
                        .map(file => ({ id: Number(file.id), path: file.path }))
                );
                const videoFile = row.videoFile as ProductMediaFile | undefined;
                setVideoFiles(videoFile?.id ? [{ id: Number(videoFile.id), path: videoFile.path }] : []);
                setPublishState(row.productGroupPurchase.publishState);
                initialEditorFileIdsRef.current =
                    (
                        row.productGroupPurchase as GroupPurchaseRow["productGroupPurchase"] & {
                            activeFileIds?: number[];
                        }
                    ).activeFileIds || [];
                const accountInfo = (
                    row.productGroupPurchase as GroupPurchaseRow["productGroupPurchase"] & {
                        accountInfo?: NonNullable<CompleteValues["platformBankAccountForm"]>;
                    }
                ).accountInfo;
                setOfflineAccounts(accountInfo?.length ? accountInfo : [{}]);
                const supplierName =
                    row.productGroupPurchase.supplierEnterprise?.supplierName ||
                    row.productGroupPurchase.supplierEnterprise?.enterpriseName;
                if (values.supplierId != null && supplierName)
                    setSupplierOptions([{ value: values.supplierId, label: supplierName }]);
                const typeName = row.productType?.areaAbbreviation
                    ? `${row.productType.name || ""} | ${row.productType.areaAbbreviation}`
                    : row.productType?.name;
                if (values.productTypeId && typeName)
                    setProductTypeOptions([
                        {
                            value: values.productTypeId,
                            label: typeName,
                            tags: row.productTypeVo?.tags?.map(tag => tag.name || "").filter(Boolean)
                        }
                    ]);
                setProductTypeTags(row.productTypeVo?.tags?.map(tag => tag.name || "").filter(Boolean) || []);
                if (values.handlerId != null && row.productGroupPurchase.handlerName)
                    setHandlerOptions([{ value: values.handlerId, label: row.productGroupPurchase.handlerName }]);
                setErrorMessage(null);
            })
            .catch(() => setErrorMessage("团购商品详情加载失败"));
    }, [form, load, loadId]);

    /** 上传前执行 Vue 相同的媒体格式和大小校验。 */
    const uploadMedia = async (
        event: ChangeEvent<HTMLInputElement>,
        type: "image" | "video",
        replaceIndex?: number
    ) => {
        const selectedFiles = Array.from(event.target.files || []);
        event.target.value = "";
        if (!selectedFiles.length) return;
        const image = type === "image";
        if (image && selectedFiles.some(file => !["image/jpeg", "image/png"].includes(file.type)))
            return setErrorMessage("主图仅支持 JPEG、PNG 格式");
        if (
            !image &&
            selectedFiles.some(file => !["video/mp4", "video/quicktime", "video/x-matroska"].includes(file.type))
        )
            return setErrorMessage("视频仅支持 MP4、MOV、MKV 格式");
        if (selectedFiles.some(file => file.size > (image ? 2 : 200) * 1024 * 1024))
            return setErrorMessage(image ? "主图大小不能超过 2MB" : "视频大小不能超过 200MB");
        try {
            const remaining = replaceIndex == null ? (image ? 6 - imageFiles.length : 1) : 1;
            const uploadedFiles: ProductMediaFile[] = [];
            for (const file of selectedFiles.slice(0, remaining)) {
                const uploaded = await uploadGroupPurchaseMedia(file);
                uploadedFiles.push({ id: uploaded.id, path: uploaded.path || String(uploaded.id) });
            }
            const currentFiles = image ? imageFiles : videoFiles;
            const nextFiles =
                replaceIndex == null
                    ? image
                        ? [...imageFiles, ...uploadedFiles]
                        : uploadedFiles
                    : currentFiles.map((file, index) =>
                          uploadedFiles[0] && index === replaceIndex ? uploadedFiles[0] : file
                      );
            form.setFieldValue(image ? "imgMainId" : "videoId", nextFiles[0]?.id);
            if (image) setImageFiles(nextFiles);
            else setVideoFiles(nextFiles);
            setErrorMessage(null);
        } catch {
            setErrorMessage(image ? "主图上传失败" : "视频上传失败");
        }
    };

    /** 第一张图片作为主图，设置主图时重排列表即可同步所有媒体字段。 */
    const setMainImage = (index: number) => {
        setImageFiles(current => {
            const selected = current[index];
            if (!selected) return current;
            const next = [selected, ...current.filter((_, itemIndex) => itemIndex !== index)];
            form.setFieldValue("imgMainId", selected.id);
            return next;
        });
    };

    /** 远程查询团购供应商。 */
    const searchSuppliers = async (keyword = "") => {
        setSupplierLoading(true);
        try {
            const data = await enterpriseManageService.selector({ supplierName: keyword });
            setSupplierOptions(data.map(item => ({ value: item.id, label: item.supplierName || "--" })));
        } catch {
            /* 保留当前回显选项。 */
        } finally {
            setSupplierLoading(false);
        }
    };

    /** 远程查询带标签的砂石品类。 */
    const searchProductTypes = async (keyword = "") => {
        setProductTypeLoading(true);
        try {
            const data = await prodTypeService.selectorVo(1, keyword);
            setProductTypeOptions(
                data.map(item => ({
                    value: item.productType.id,
                    label: item.productType.areaAbbreviation
                        ? `${item.productType.name} | ${item.productType.areaAbbreviation}`
                        : item.productType.name,
                    specificationModulus: item.productType.specificationModulus,
                    tags: item.tags.map(tag => tag.name)
                }))
            );
        } catch {
            /* 保留当前回显选项。 */
        } finally {
            setProductTypeLoading(false);
        }
    };

    /** 远程查询商品专员。 */
    const searchHandlers = async (keyword = "") => {
        setHandlerLoading(true);
        try {
            const data = await groupPurchaseService.findServiceSpecials(keyword);
            setHandlerOptions(
                data.map(item => ({
                    value: item.user.personId,
                    label: item.user.employeeId ? `${item.user.name} | ${item.user.employeeId}` : item.user.name
                }))
            );
        } catch {
            /* 保留当前回显选项。 */
        } finally {
            setHandlerLoading(false);
        }
    };

    /** 把动态条目、坐标与支付方式还原为后端契约。 */
    const buildPayload = (values: CompleteValues): GroupPurchaseFormPayload => {
        const payload = Object.fromEntries(
            Object.entries(values).filter(([, value]) => value !== undefined)
        ) as GroupPurchaseFormPayload;
        if (payload.geoLng !== undefined || payload.geoLat !== undefined) {
            payload.geo = {
                lng: payload.geoLng ? Number(payload.geoLng) : undefined,
                lat: payload.geoLat ? Number(payload.geoLat) : undefined
            };
            delete payload.geoLng;
            delete payload.geoLat;
        }
        if (payload.payTypes)
            payload.payType = payload.payTypes.includes(1) && payload.payTypes.includes(2) ? 3 : payload.payTypes[0];
        payload.imgMainId = imageFiles[0]?.id ?? payload.imgMainId;
        payload.imgIds = imageFiles.slice(1).map(file => file.id);
        payload.videoId = videoFiles[0]?.id ?? payload.videoId;
        const editorIds = [
            ...extractProductEditorFileIds(payload.introduce),
            ...(introduceEditorRef.current
                ? (["image", "video"] as const).flatMap(type =>
                      introduceEditorRef
                          .current!.editor.getElemsByType(type)
                          .map(item => introduceEditorRef.current!.files.get(item.src))
                          .filter((fileId): fileId is number => Boolean(fileId))
                  )
                : [])
        ];
        // 旧详情若正文 URL 已失去 #id，则沿用后端 activeFileIds，避免保存复制时解除历史媒体关联。
        if (!editorIds.length && payload.introduce) editorIds.push(...initialEditorFileIdsRef.current);
        // activeFileIds 只用于富文本媒体生命周期，商品图片与视频分别由媒体字段维护。
        payload.activeFileIds = Array.from(new Set(editorIds));
        payload.platformBankAccountForm = payload.payTypes?.includes(2) ? offlineAccounts : undefined;
        return payload;
    };

    const submit = async (draft: boolean) => {
        // 提交前清理旧提示，保证保存草稿与正式提交之间不会复用旧错误。
        setErrorMessage(null);
        try {
            // 草稿允许字段不完整；正式提交才执行完整必填校验。
            const values = draft ? form.getFieldsValue() : await form.validateFields();
            const contractError = validateLegacyProductDraft(values, "groupPurchase", {
                draft,
                editing: Boolean(id)
            });
            if (contractError) {
                setErrorMessage(contractError);
                return;
            }
            if (
                !draft &&
                (!values.handlerMobile?.length || values.handlerMobile.some(item => !/^[0-9-]{1,18}$/.test(item)))
            ) {
                setErrorMessage(values.handlerMobile?.some(Boolean) ? "请输入正确的手机号" : "请输入商品专员联系电话");
                return;
            }
            if (
                values.parameterInfo?.some(
                    item => (item.parameterName?.length || 0) > 10 || (item.parameterValue?.length || 0) > 10
                )
            )
                return setErrorMessage("参数名称和参数值不能超过10个字符");
            if (!draft && values.parameterInfo?.some(item => !item.parameterName || !item.parameterValue))
                return setErrorMessage("请输入参数值");
            if (values.productLabel?.some(label => label.length > 6)) return setErrorMessage("商品标签不能超过6个字符");
            if (!draft && values.productLabel?.some(label => !label)) return setErrorMessage("请输入商品标签");
            if (!draft) {
                const requiredFields: Array<[unknown, string]> = [
                    [values.title, "请输入商品标题"],
                    [values.priceType, "请选择单价类型"],
                    [values.groupUnitPrice, "请输入拼团单价"],
                    [values.unitPrice, "请输入原价"],
                    [imageFiles.length, "请上传图片"],
                    [videoFiles.length, "请上传视频"],
                    [values.supplierId, "请选择供应商"],
                    [values.productTypeId, "请选择品类"],
                    [values.productTypeSpec, "请输入规格"],
                    [values.introduce, "请输入商品详情"],
                    [values.groupEndTime, "请选择拼团截止时间"],
                    [values.deliveryStartTime && values.deliveryEndTime, "请选择提货周期"],
                    [values.regionCode, "请选择交付地点"],
                    [values.address, "请输入详细地址"],
                    [values.geoLng && values.geoLat, "请选择经纬度"],
                    [values.minOrderTon, "请输入拼团最低吨数"],
                    [values.groupSuccessTon, "请输入拼团成功吨数"],
                    [values.minAdjustmentTon, "请输入最小调整吨数"],
                    [values.settlementMethod, "请选择结算方式"],
                    [values.payTypes?.length, "请选择支付方式"],
                    [values.regulation, "请输入团购规则"],
                    [values.handlerId, "请选择商品专员"],
                    [values.waitingPayTime, "请选择等待付款时间"]
                ];
                const missing = requiredFields.find(
                    ([value]) => value === undefined || value === null || value === "" || value === 0
                );
                if (missing) {
                    setErrorMessage(missing[1]);
                    return;
                }
            }
            // 与旧 Vue 的条件校验保持一致，选择支付定金后必须补齐支付方式和对应金额。
            if (!draft && values.settlementMethod === 2) {
                if (!values.depositPaymentMethod) return setErrorMessage("请选择保证金支付方式");
                if (
                    values.depositPaymentMethod === 1 &&
                    (values.paymentRatio == null || values.paymentRatio < 0 || values.paymentRatio > 99)
                ) {
                    return setErrorMessage(
                        values.paymentRatio == null ? "请输入货款比例" : "货款比例为大于等于0，小于100的整数"
                    );
                }
                if (values.depositPaymentMethod === 2 && (!values.fixedAmount || values.fixedAmount > 999999)) {
                    return setErrorMessage(
                        values.fixedAmount ? "固定金额为大于0，小于1000000的整数" : "请输入固定金额"
                    );
                }
            }
            if (!draft && values.isIncludeTax === 1 && !values.taxRate) return setErrorMessage("请选择税率");
            if (!draft && values.minOrderTon && values.groupSuccessTon && values.minOrderTon > values.groupSuccessTon)
                return setErrorMessage("拼团最低吨数不能大于拼团成功吨数");
            if (!draft && values.minAdjustmentTon && values.minOrderTon && values.minAdjustmentTon > values.minOrderTon)
                return setErrorMessage("最小调整吨数不能大于拼团最低吨数");
            if (
                !draft &&
                values.floatType === 2 &&
                values.groupFloatTon &&
                values.groupSuccessTon &&
                values.groupFloatTon > values.groupSuccessTon
            )
                return setErrorMessage("浮动固定吨数不能大于拼团成功吨数");
            if (
                !draft &&
                values.deliveryStartTime &&
                values.deliveryEndTime &&
                values.deliveryStartTime > values.deliveryEndTime
            )
                return setErrorMessage("提货结束日期不能早于提货开始日期");
            if (!draft && values.groupEndTime && new Date(values.groupEndTime).getTime() < Date.now())
                return setErrorMessage("拼团截止时间不能选择当前之前的时间");
            if (!draft && values.payTypes?.includes(2) && offlineAccounts.some(account => !account.account))
                return setErrorMessage("请选择收款账户");
            await (draft ? save : submitForm)(id, buildPayload(values));
            notifyOperationSuccess(draft ? "已保存" : "已提交");
            void navigate("/productManagement/groupPurchase");
        } catch (error) {
            // Ant Form 校验失败已在字段旁展示具体原因，不再追加泛化的保存失败提示。
            if (!(error as { errorFields?: unknown[] }).errorFields) setErrorMessage("团购商品保存失败");
        }
    };

    const openMapPicker = () => {
        const point = parseGeoPoint(
            [form.getFieldValue("geoLng"), form.getFieldValue("geoLat")].filter(Boolean).join(",")
        );
        setMapData({
            id: Number(id) || 0,
            areaCode: [
                form.getFieldValue("provinceCode"),
                form.getFieldValue("cityCode"),
                form.getFieldValue("regionCode")
            ].filter(Boolean),
            address: form.getFieldValue("address") || "",
            province: "",
            city: "",
            region: "",
            detailAddress: form.getFieldValue("address") || "",
            lngLat: point ? `${point[0]},${point[1]}` : "",
            draggable: true
        });
        setMapDialogOpen(true);
    };

    /** 旧 address-item 的地图确认会整体覆盖地区、详细地址与坐标。 */
    const confirmMapPicker = (data: MapViewForm) => {
        const point = parseGeoPoint(data.lngLat);
        if (!point) return setErrorMessage("请选择有效经纬度");
        form.setFieldsValue({
            provinceCode: data.areaCode?.[0],
            cityCode: data.areaCode?.[1],
            regionCode: data.areaCode?.[2],
            address: data.detailAddress || data.address || "",
            geoLng: String(point[0]),
            geoLat: String(point[1])
        });
        setMapDialogOpen(false);
    };

    return (
        <section className="consignment-form-page" aria-label="group-purchase-form-page">
            <section className="section-card consignment-form__header">
                <ZhDetailHeader title={id ? "编辑团购商品" : "新增团购商品"} />
            </section>
            {errorMessage ? <Alert className="mb-2" message={errorMessage} role="alert" type="error" /> : null}
            <Form
                form={form}
                autoComplete="off"
                className="consignment-form__form"
                colon={false}
                labelAlign="right"
                labelCol={{ flex: "128px" }}
                wrapperCol={{ flex: 1 }}
            >
                <FormSection title="商品信息">
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <ZhGridItem>
                            <Form.Item
                                label="商品标题"
                                name="title"
                                required
                                rules={[{ required: true, message: "请输入商品标题" }, { max: 30 }]}
                            >
                                <Input placeholder="请输入商品名称" allowClear />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="权重值" name="weight">
                                <InputNumber
                                    className="w-full"
                                    min={0}
                                    max={10000}
                                    controls={false}
                                    placeholder="请输入权重值"
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <div className="consignment-form__price-row">
                                <label className="consignment-form__label consignment-form__label--required">
                                    拼团单价
                                </label>
                                <div className="consignment-form__price-control">
                                    <Radio.Group
                                        value={priceType}
                                        onChange={event => form.setFieldValue("priceType", event.target.value)}
                                    >
                                        <Radio value={1}>
                                            <span className="consignment-form__price-radio">
                                                固定价格 <PriceTip type={1} />
                                            </span>
                                        </Radio>
                                        <Radio value={2}>
                                            <span className="consignment-form__price-radio">
                                                预估价格 <PriceTip type={2} />
                                            </span>
                                        </Radio>
                                    </Radio.Group>
                                    <Form.Item
                                        name="groupUnitPrice"
                                        noStyle
                                        rules={[{ required: true, message: "请输入拼团单价" }]}
                                    >
                                        <InputNumber
                                            aria-label="拼团单价"
                                            min={0.01}
                                            max={10000}
                                            precision={2}
                                            controls={false}
                                            placeholder="请输入拼团单价"
                                            addonAfter="元/吨"
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="是否含税" required>
                                {Number(isIncludeTax) === 0 ? (
                                    <Form.Item name="isIncludeTax" noStyle>
                                        <Select
                                            aria-label="是否含税"
                                            placeholder="请选择是否含税"
                                            options={[
                                                { label: "不含", value: 0 },
                                                { label: "含税", value: 1 }
                                            ]}
                                            onChange={value => {
                                                form.setFieldValue("isIncludeTax", value);
                                                if (value !== 1) form.setFieldValue("taxRate", undefined);
                                            }}
                                        />
                                    </Form.Item>
                                ) : (
                                    <Space.Compact className="w-full">
                                        <Form.Item name="isIncludeTax" noStyle>
                                            <Select
                                                aria-label="是否含税"
                                                placeholder="请选择是否含税"
                                                options={[
                                                    { label: "不含", value: 0 },
                                                    { label: "含税", value: 1 }
                                                ]}
                                                onChange={value => {
                                                    form.setFieldValue("isIncludeTax", value);
                                                    if (value !== 1) form.setFieldValue("taxRate", undefined);
                                                }}
                                            />
                                        </Form.Item>
                                        <Form.Item name="taxRate" noStyle>
                                            <Select
                                                aria-label="税率"
                                                placeholder="请选择税率"
                                                options={[3, 6, 9, 13].map((label, index) => ({
                                                    label: `${label}%`,
                                                    value: index + 1
                                                }))}
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                )}
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="原价" name="unitPrice" required>
                                <InputNumber
                                    className="w-full"
                                    min={0.01}
                                    max={10000}
                                    precision={2}
                                    controls={false}
                                    placeholder="请输入原价"
                                    addonAfter="元/吨"
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="拼团截止时间" name="groupEndTime" required>
                                <ZhDatePicker
                                    className="w-full"
                                    disabledDate={date => date.getTime() < new Date().setHours(0, 0, 0, 0)}
                                    format="YYYY-MM-DD HH:mm"
                                    placeholder="请选择拼团截止时间"
                                    type="datetime"
                                    valueFormat="YYYY-MM-DD HH:mm:ss"
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="等待付款时间" name="waitingPayTime" required>
                                <Select
                                    aria-label="等待付款时间"
                                    placeholder="请选择等待付款时间"
                                    options={[
                                        "3小时",
                                        "12小时",
                                        "24小时",
                                        "48小时",
                                        "72小时",
                                        "5天",
                                        "7天",
                                        "不限时长"
                                    ].map((label, index) => ({ label, value: index + 1 }))}
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="拼团最低吨数" name="minOrderTon" required>
                                <InputNumber
                                    className="w-full"
                                    min={1}
                                    max={10000000}
                                    controls={false}
                                    placeholder="请输入拼团最低吨数"
                                    addonAfter="吨"
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <div className="consignment-form__group-ton-row">
                                <Form.Item
                                    className="consignment-form__group-success-ton"
                                    label="拼团成功吨数"
                                    name="groupSuccessTon"
                                    required
                                >
                                    <InputNumber
                                        className="w-full"
                                        min={1}
                                        max={10000000}
                                        controls={false}
                                        placeholder="请输入拼团成功吨数"
                                        addonAfter="吨"
                                    />
                                </Form.Item>
                                <Form.Item className="consignment-form__float-ton" label="浮动方式" required>
                                    <Space.Compact className="w-full">
                                        <Form.Item name="floatType" noStyle>
                                            <Select
                                                aria-label="浮动方式"
                                                placeholder="请选择浮动方式"
                                                options={[
                                                    { label: "浮动比例", value: 1 },
                                                    { label: "浮动固定吨数", value: 2 }
                                                ]}
                                                onChange={value =>
                                                    form.setFieldsValue({
                                                        floatType: value,
                                                        groupFloatRatio: undefined,
                                                        groupFloatTon: undefined
                                                    })
                                                }
                                            />
                                        </Form.Item>
                                        {Number(floatType) === 1 ? (
                                            <Form.Item name="groupFloatRatio" noStyle>
                                                <InputNumber
                                                    aria-label="浮动比例"
                                                    min={0}
                                                    max={99}
                                                    placeholder="请输入浮动比例"
                                                    addonAfter="%"
                                                />
                                            </Form.Item>
                                        ) : (
                                            <Form.Item name="groupFloatTon" noStyle>
                                                <InputNumber
                                                    aria-label="浮动固定吨数"
                                                    min={0}
                                                    max={10000000}
                                                    placeholder="请输入浮动固定吨数"
                                                    addonAfter="吨"
                                                />
                                            </Form.Item>
                                        )}
                                    </Space.Compact>
                                </Form.Item>
                            </div>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <div className="consignment-form__media-row">
                                <label className="consignment-form__label consignment-form__label--required">
                                    图片
                                </label>
                                <ProductMediaUpload
                                    type="image"
                                    files={imageFiles}
                                    inputIdPrefix="group-purchase"
                                    onUpload={(event, replaceIndex) => void uploadMedia(event, "image", replaceIndex)}
                                    onSetMain={setMainImage}
                                    onRemove={index => {
                                        const next = imageFiles.filter((_, fileIndex) => fileIndex !== index);
                                        setImageFiles(next);
                                        form.setFieldValue("imgMainId", next[0]?.id);
                                    }}
                                />
                                <Form.Item name="imgMainId" noStyle>
                                    <Input className="consignment-form__legacy-control" />
                                </Form.Item>
                            </div>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <div className="consignment-form__media-row">
                                <label className="consignment-form__label consignment-form__label--required">
                                    视频
                                </label>
                                <ProductMediaUpload
                                    type="video"
                                    files={videoFiles}
                                    inputIdPrefix="group-purchase"
                                    onUpload={(event, replaceIndex) => void uploadMedia(event, "video", replaceIndex)}
                                    onRemove={() => {
                                        setVideoFiles([]);
                                        form.setFieldValue("videoId", undefined);
                                    }}
                                />
                                <Form.Item name="videoId" noStyle>
                                    <Input className="consignment-form__legacy-control" />
                                </Form.Item>
                            </div>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="供应商" required>
                                <Select
                                    allowClear
                                    showSearch
                                    filterOption={false}
                                    loading={supplierLoading}
                                    options={supplierOptions}
                                    value={supplierId}
                                    placeholder="请选择供应商"
                                    onChange={value => form.setFieldValue("supplierId", value)}
                                    onSearch={value => void searchSuppliers(value)}
                                />
                                <Form.Item name="supplierId" noStyle>
                                    <Input className="consignment-form__legacy-control" />
                                </Form.Item>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="品类" required>
                                <Select
                                    allowClear
                                    showSearch
                                    filterOption={false}
                                    loading={productTypeLoading}
                                    options={productTypeOptions}
                                    value={productTypeId}
                                    placeholder="请选择砂石品类"
                                    onChange={value => {
                                        form.setFieldValue("productTypeId", value);
                                        const selected = productTypeOptions.find(option => option.value === value);
                                        setProductTypeTags(selected?.tags || []);
                                        if (selected?.specificationModulus)
                                            form.setFieldValue("productTypeSpec", selected.specificationModulus);
                                    }}
                                    onSearch={value => void searchProductTypes(value)}
                                />
                                <Form.Item name="productTypeId" noStyle>
                                    <Input className="consignment-form__legacy-control" />
                                </Form.Item>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="规格" name="productTypeSpec" required>
                                <Input />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="标签">
                                <Input
                                    aria-label="品类标签"
                                    disabled
                                    value={productTypeTags.length ? productTypeTags.join(",") : "--"}
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Form.Item label="参数值" name="parameterInfo">
                                <ParameterEditor />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Form.Item label="商品标签" name="productLabel">
                                <ProductLabelEditor />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <RichTextItem
                                form={form}
                                name="introduce"
                                label="商品介绍"
                                required
                                editorRef={introduceEditorRef}
                            />
                        </ZhGridItem>
                    </ZhGrid>
                </FormSection>
                <FormSection title="提货信息">
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <ZhGridItem>
                            <Form.Item label="提货周期" required>
                                {id && Number(publishState) !== 6 ? (
                                    <div className="consignment-form__delivery-time-edit">
                                        <span>{formatDeliveryDateValue(form.getFieldValue("deliveryStartTime"))}</span>
                                        <span>至</span>
                                        <Form.Item name="deliveryEndTime" noStyle>
                                            <ZhDatePicker
                                                aria-label="提货结束时间"
                                                className="w-full"
                                                disabledDate={date => date.getTime() < new Date().setHours(0, 0, 0, 0)}
                                                format="YYYY-MM-DD"
                                                placeholder="请选择提货结束日期"
                                                type="date"
                                                valueFormat="YYYY-MM-DD"
                                            />
                                        </Form.Item>
                                    </div>
                                ) : (
                                    <Space.Compact className="w-full">
                                        <Form.Item name="deliveryStartTime" noStyle>
                                            <ZhDatePicker
                                                aria-label="提货开始时间"
                                                className="w-full"
                                                disabledDate={date => date.getTime() < new Date().setHours(0, 0, 0, 0)}
                                                format="YYYY-MM-DD"
                                                placeholder="请选择提货开始日期"
                                                type="date"
                                                valueFormat="YYYY-MM-DD"
                                            />
                                        </Form.Item>
                                        <Form.Item name="deliveryEndTime" noStyle>
                                            <ZhDatePicker
                                                aria-label="提货结束时间"
                                                className="w-full"
                                                disabledDate={date => date.getTime() < new Date().setHours(0, 0, 0, 0)}
                                                format="YYYY-MM-DD"
                                                placeholder="请选择提货结束日期"
                                                type="date"
                                                valueFormat="YYYY-MM-DD"
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                )}
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="提货地点" required>
                                <Cascader
                                    aria-label="交付地点"
                                    className="w-full"
                                    options={areaOptions}
                                    fieldNames={{ label: "name", value: "id", children: "childs" }}
                                    value={[
                                        form.getFieldValue("provinceCode"),
                                        form.getFieldValue("cityCode"),
                                        form.getFieldValue("regionCode")
                                    ].filter(Boolean)}
                                    onFocus={() => void ensureAreaOptions()}
                                    onChange={value =>
                                        form.setFieldsValue({
                                            provinceCode: value[0] == null ? undefined : String(value[0]),
                                            cityCode: value[1] == null ? undefined : String(value[1]),
                                            regionCode: value[2] == null ? undefined : String(value[2])
                                        })
                                    }
                                />
                                {(["provinceCode", "cityCode", "regionCode"] as const).map((field, index) => (
                                    <Form.Item name={field} noStyle key={field}>
                                        <Input
                                            aria-label={["省编码", "市编码", "区编码"][index]}
                                            className="consignment-form__legacy-control"
                                        />
                                    </Form.Item>
                                ))}
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item required>
                                <Form.Item name="address" noStyle>
                                    <ProductAddressAutocomplete
                                        ariaLabel="详细地址"
                                        regionCode={regionCode}
                                        loadSuggestions={loadAddressSuggestions}
                                        onSelectSuggestion={selectAddressSuggestion}
                                    />
                                </Form.Item>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="经纬度" required>
                                <button
                                    aria-label="经纬度"
                                    className="w-full rounded border border-slate-200 px-3 py-1.5 text-left disabled:cursor-not-allowed disabled:text-slate-400"
                                    disabled={!regionCode}
                                    type="button"
                                    onClick={openMapPicker}
                                >
                                    {geoLng && geoLat
                                        ? `${geoLng}, ${geoLat}`
                                        : regionCode
                                          ? "请选择经纬度"
                                          : "请先选择区域"}
                                </button>
                                <Form.Item name="geoLng" noStyle>
                                    <input aria-label="经度" type="hidden" />
                                </Form.Item>
                                <Form.Item name="geoLat" noStyle>
                                    <input aria-label="纬度" type="hidden" />
                                </Form.Item>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="最小调整吨数" name="minAdjustmentTon" required>
                                <InputNumber
                                    className="w-full"
                                    min={1}
                                    max={10000000}
                                    placeholder="请输入最小调整吨数"
                                    addonAfter="吨"
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <RichTextItem form={form} name="deliveryDescn" label="提货说明" />
                        </ZhGridItem>
                    </ZhGrid>
                </FormSection>
                <FormSection title="结算信息">
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <ZhGridItem span={2}>
                            <Form.Item label="下单要求" name="settlementMethod" required>
                                <div className="consignment-form__settlement-order">
                                    <Radio.Group
                                        options={[
                                            { label: "全部货款", value: 1 },
                                            { label: "支付定金", value: 2 }
                                        ]}
                                        onChange={event =>
                                            form.setFieldsValue({
                                                settlementMethod: event.target.value,
                                                depositPaymentMethod: undefined,
                                                paymentRatio: undefined,
                                                fixedAmount: undefined
                                            })
                                        }
                                    />
                                    {Number(settlementMethod) === 2 ? (
                                        <Space.Compact className="consignment-form__deposit-controls">
                                            <Form.Item name="depositPaymentMethod" noStyle>
                                                <Select
                                                    aria-label="支付定金方式选择"
                                                    placeholder="请选择支付定金方式"
                                                    options={[
                                                        { label: "货款比例", value: 1 },
                                                        { label: "固定金额", value: 2 }
                                                    ]}
                                                    onChange={value =>
                                                        form.setFieldsValue({
                                                            depositPaymentMethod: value,
                                                            paymentRatio: undefined,
                                                            fixedAmount: undefined
                                                        })
                                                    }
                                                />
                                            </Form.Item>
                                            {Number(depositPaymentMethod) === 1 ? (
                                                <Form.Item name="paymentRatio" noStyle>
                                                    <InputNumber
                                                        controls={false}
                                                        max={99}
                                                        min={1}
                                                        placeholder="请输入货款比例"
                                                        suffix="%"
                                                    />
                                                </Form.Item>
                                            ) : (
                                                <Form.Item name="fixedAmount" noStyle>
                                                    <InputNumber
                                                        controls={false}
                                                        max={999999}
                                                        min={1}
                                                        placeholder="请输入固定金额"
                                                        suffix="元"
                                                    />
                                                </Form.Item>
                                            )}
                                        </Space.Compact>
                                    ) : null}
                                </div>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <Form.Item label="支付方式" name="payTypes" required>
                                <Checkbox.Group
                                    options={[
                                        {
                                            label: (
                                                <span className="consignment-form__pay-type-label">
                                                    线上支付 <OnlineAccountTip />
                                                </span>
                                            ),
                                            value: 1
                                        },
                                        { label: "线下支付", value: 2 }
                                    ]}
                                />
                            </Form.Item>
                        </ZhGridItem>
                        {payTypes?.includes(2) ? (
                            <ZhGridItem span={4}>
                                <OfflineAccountEditor value={offlineAccounts} onChange={setOfflineAccounts} />
                            </ZhGridItem>
                        ) : null}
                        <ZhGridItem span={4}>
                            <RichTextItem form={form} name="settlementDescn" label="结算说明" />
                        </ZhGridItem>
                    </ZhGrid>
                </FormSection>
                <FormSection title="服务保障">
                    <RichTextItem form={form} name="serviceGuarantee" label="服务保障" />
                    <RichTextItem form={form} name="regulation" label="团购规则" required />
                </FormSection>
                <FormSection title="其他信息">
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <ZhGridItem>
                            <Form.Item label="商品专员" required>
                                <Select
                                    aria-label="商品专员"
                                    allowClear
                                    showSearch
                                    filterOption={false}
                                    loading={handlerLoading}
                                    disabled={Boolean(id && Number(publishState) !== 6)}
                                    options={handlerOptions}
                                    value={handlerId}
                                    placeholder="请选择商品专员"
                                    onChange={value => form.setFieldValue("handlerId", value)}
                                    onSearch={value => void searchHandlers(value)}
                                />
                                <Form.Item name="handlerId" noStyle>
                                    <Input className="consignment-form__legacy-control" />
                                </Form.Item>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={3}>
                            <Form.Item
                                label="商品专员联系电话"
                                name="handlerMobile"
                                rules={[
                                    {
                                        validator: (_, value: string[] | undefined) =>
                                            !value?.length || value.some(item => !/^[0-9-]{1,18}$/.test(item))
                                                ? Promise.reject(new Error("请输入正确的手机号"))
                                                : Promise.resolve()
                                    }
                                ]}
                            >
                                <HandlerMobileEditor />
                            </Form.Item>
                        </ZhGridItem>
                    </ZhGrid>
                </FormSection>
            </Form>
            <Space className="operation-fixed-btn consignment-form__actions" data-testid="group-purchase-form-actions">
                <Button aria-label="保存团购商品" type="primary" onClick={() => void submit(true)}>
                    保存
                </Button>
                <Button aria-label="提交团购商品" type="primary" onClick={() => void submit(false)}>
                    提交
                </Button>
                <Button onClick={() => void navigate("/productManagement/groupPurchase")}>返回</Button>
            </Space>
            {mapDialogOpen ? (
                <ZhMapDialog
                    data={mapData}
                    formType={id ? FormTypeEnum.Edit : FormTypeEnum.Add}
                    pointerMarker
                    visible={mapDialogOpen}
                    onSubmit={confirmMapPicker}
                    onUpdateData={setMapData}
                    onUpdateVisible={setMapDialogOpen}
                />
            ) : null}
        </section>
    );
}

export default GroupPurchaseFormPage;
